import {Box, Checkbox, FormControlLabel, Grid, Table, TableBody, TableCell, TableRow, Typography} from '@mui/material';
import React from 'react';
import {useFormContext} from 'react-hook-form';
import {i18n} from 'src/i18n';
import {toMedicionSelectOptions} from 'src/modules/tarea/medicionesParcialId23';
import {
  CONTROL_VISUAL_GOMA_TEFLON_OPTIONS,
  ID24_COLOR_FUNDA_OPTIONS,
  ID24_MAQUINA_SELLADORA_OPTIONS,
  ID24_MICROPERFORADO_OPTIONS,
  ID24_PREPICADO_OPTIONS,
  ID24_TIPO_SELLO_OPTIONS,
  ID24_TROQUEL_OPTIONS,
  TIPO_EMBALAJE_OPTIONS,
} from 'src/modules/tarea/medicionesParcialId24';
import CheckboxFormItem from 'src/view/shared/form/items/CheckboxFormItem';
import InputNumberFormItem from 'src/view/shared/form/items/InputNumberFormItem';
import SelectFormItem from 'src/view/shared/form/items/SelectFormItem';

const cellSx = {
  border: '1px solid',
  borderColor: 'divider',
  p: 0.5,
  verticalAlign: 'middle',
};

const titleSx = {fontWeight: 700, fontSize: 13, textTransform: 'uppercase', mb: 0.5};

const compactInputSx = {
  '& .MuiInputBase-root': {fontSize: 13},
  '& .MuiInputBase-input': {py: 0.75, px: 1},
};

function MedicionInput({
  name,
  disabled,
  required,
  notApplicable,
}: {
  name: string;
  disabled?: boolean;
  required?: boolean;
  notApplicable?: boolean;
}) {
  if (notApplicable) {
    return (
      <Typography sx={{fontSize: 13, textAlign: 'center', py: 0.75}}>
        {i18n('common.notApplicable')}
      </Typography>
    );
  }
  return (
    <InputNumberFormItem
      name={name}
      label=""
      required={Boolean(required)}
      disabled={disabled}
      disableFormat
      size="small"
      style={compactInputSx}
    />
  );
}

function MedicionSelect({
  name,
  catalog,
  disabled,
  required,
}: {
  name: string;
  catalog: readonly string[];
  disabled?: boolean;
  required?: boolean;
}) {
  const {watch} = useFormContext();
  const current = watch(name);
  return (
    <SelectFormItem
      name={name}
      label=""
      required={Boolean(required)}
      disabled={disabled}
      options={toMedicionSelectOptions(catalog, current)}
      isClearable={!required}
      selectAllButton={false}
      margin="none"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
    />
  );
}

function NaCheckbox({name, disabled}: {name: string; disabled?: boolean}) {
  return (
    <CheckboxFormItem
      name={name}
      label={i18n('common.notApplicable')}
      disabled={disabled}
      size="small"
      style={{marginTop: -6, marginBottom: -6, marginRight: 0}}
    />
  );
}

function SectionHeader({
  title,
  required,
  naName,
  disabled,
}: {
  title: string;
  required?: boolean;
  naName?: string;
  disabled?: boolean;
}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5, minHeight: 28}}>
      <Typography sx={{...titleSx, mb: 0} as any}>
        {title}
        {required ? (
          <Box component="span" sx={{color: 'error.main'}}>
            {' *'}
          </Box>
        ) : null}
      </Typography>
      {naName ? <NaCheckbox name={naName} disabled={disabled} /> : null}
    </Box>
  );
}

function SectionTable({
  title,
  required,
  naName,
  disabled,
  children,
}: {
  title: string;
  required?: boolean;
  naName?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const {watch} = useFormContext();
  const isNa = naName ? Boolean(watch(naName)) : false;
  return (
    <Box>
      <SectionHeader title={title} required={required} naName={naName} disabled={disabled} />
      <Box sx={isNa ? {opacity: 0.55, pointerEvents: 'none'} : undefined}>
        <Table size="small" sx={{borderCollapse: 'collapse'}}>
          <TableBody>{children}</TableBody>
        </Table>
      </Box>
    </Box>
  );
}

function StringArrayCheckboxGroup({
  name,
  options,
  labels,
  disabled,
  exclusive,
}: {
  name: string;
  options: readonly string[];
  labels: Record<string, string>;
  disabled?: boolean;
  exclusive?: boolean;
}) {
  const {watch, setValue} = useFormContext();
  const selected: string[] = Array.isArray(watch(name)) ? watch(name) : [];

  const toggle = (option: string, checked: boolean) => {
    const next = checked
      ? exclusive
        ? [option]
        : [...selected.filter((v) => v !== option), option]
      : selected.filter((v) => v !== option);
    setValue(name, next, {shouldDirty: true, shouldValidate: true});
  };

  return (
    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, px: 0.5}}>
      {options.map((option) => (
        <FormControlLabel
          key={option}
          control={
            <Checkbox
              size="small"
              checked={selected.includes(option)}
              disabled={disabled}
              onChange={(e) => toggle(option, e.target.checked)}
            />
          }
          label={<Typography sx={{fontSize: 13, fontWeight: 600}}>{labels[option] || option}</Typography>}
        />
      ))}
    </Box>
  );
}

function MedicionesParcialId24Section({disabled}: {disabled?: boolean}) {
  const {watch} = useFormContext();
  const baseName = 'medicionesParcial';

  if (!watch('primeroDelParcial')) {
    return null;
  }

  const naPerforaciones = Boolean(watch(`${baseName}.na_cant_perforaciones`));
  const naPrepicado = Boolean(watch(`${baseName}.na_prepicado`));
  const naControlVisual = Boolean(watch(`${baseName}.na_control_visual_goma_teflon`));
  const naEmbalaje = Boolean(watch(`${baseName}.na_tipo_embalaje`));

  const controlVisualLabels = {
    INICIO: i18n('entities.tarea.medicionesParcial.inicio'),
    TERMINO: i18n('entities.tarea.medicionesParcial.termino'),
  };
  const tipoEmbalajeLabels = {
    SACO: i18n('entities.tarea.medicionesParcial.saco'),
    BOLSA_FUNDA: i18n('entities.tarea.medicionesParcial.bolsaFunda'),
  };

  return (
    <Box mb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.troquel')} required disabled={disabled}>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionSelect name={`${baseName}.troquel`} catalog={ID24_TROQUEL_OPTIONS} disabled={disabled} required />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable
            title={i18n('entities.tarea.medicionesParcial.cantPerforaciones')}
            required
            naName={`${baseName}.na_cant_perforaciones`}
            disabled={disabled}>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionInput
                  name={`${baseName}.cant_perforaciones`}
                  disabled={disabled}
                  notApplicable={naPerforaciones}
                />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.microperforado')} required disabled={disabled}>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionSelect
                  name={`${baseName}.microperforado`}
                  catalog={ID24_MICROPERFORADO_OPTIONS}
                  disabled={disabled}
                  required
                />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.tipoSello')} required disabled={disabled}>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionSelect name={`${baseName}.tipo_sello`} catalog={ID24_TIPO_SELLO_OPTIONS} disabled={disabled} required />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable
            title={i18n('entities.tarea.medicionesParcial.prepicado')}
            required
            naName={`${baseName}.na_prepicado`}
            disabled={disabled}>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionSelect
                  name={`${baseName}.prepicado`}
                  catalog={ID24_PREPICADO_OPTIONS}
                  disabled={disabled || naPrepicado}
                  required
                />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.colorFunda')} required disabled={disabled}>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionSelect name={`${baseName}.color_funda`} catalog={ID24_COLOR_FUNDA_OPTIONS} disabled={disabled} required />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.maquinaSelladora')} required disabled={disabled}>
            <TableRow>
              <TableCell sx={cellSx}>
                <MedicionSelect
                  name={`${baseName}.maquina_selladora`}
                  catalog={ID24_MAQUINA_SELLADORA_OPTIONS}
                  disabled={disabled}
                  required
                />
              </TableCell>
            </TableRow>
          </SectionTable>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box>
            <SectionHeader
              title={i18n('entities.tarea.medicionesParcial.controlVisualGomaTeflon')}
              required
              naName={`${baseName}.na_control_visual_goma_teflon`}
              disabled={disabled}
            />
            <Box
              sx={{
                ...cellSx,
                border: '1px solid',
                borderColor: 'divider',
                ...(naControlVisual ? {opacity: 0.55, pointerEvents: 'none'} : {}),
              }}>
              <StringArrayCheckboxGroup
                name={`${baseName}.control_visual_goma_teflon`}
                options={CONTROL_VISUAL_GOMA_TEFLON_OPTIONS}
                labels={controlVisualLabels}
                disabled={disabled || naControlVisual}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box>
            <SectionHeader
              title={i18n('entities.tarea.medicionesParcial.tipoEmbalaje')}
              required
              naName={`${baseName}.na_tipo_embalaje`}
              disabled={disabled}
            />
            <Box
              sx={{
                ...cellSx,
                border: '1px solid',
                borderColor: 'divider',
                ...(naEmbalaje ? {opacity: 0.55, pointerEvents: 'none'} : {}),
              }}>
              <StringArrayCheckboxGroup
                name={`${baseName}.tipo_embalaje`}
                options={TIPO_EMBALAJE_OPTIONS}
                labels={tipoEmbalajeLabels}
                disabled={disabled || naEmbalaje}
                exclusive
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default MedicionesParcialId24Section;
